<script lang="ts" setup>
import {cx} from 'class-variance-authority'
import {lexicalToPlaintext} from '~/utils/lexical'
import {resolveRelations} from '~/utils/relations'
import Solutions from '~/components/logistics/solutions/solutions.vue'
import ShadowBorder from '~/components/managed-projects/shadow-border.vue'
import HealthCards from '~/components/healthcare/health-cards.vue'
import MoreSection from '~/components/healthcare/more-section.vue'
import SingleTestimonial from '~/components/managed-projects/single-testimonial.vue'
import Benefit from '~/components/healthcare/benefit.vue'
import Comparison from '~/components/healthcare/comparison.vue'
import Accordion from '~/components/ui/accordion.vue'
import BlogSection from '~/components/workshop/blog-section.vue'
import ReviewsSmall from '~/components/workshop/reviews-small.vue'

const {
  public: {baseUrl},
} = useRuntimeConfig()

const lp = useLocalePath()

const {fetch} = useHealthcare()
const {data: initialData} = await useAsyncData('healthcare', () => fetch())
const {data: healthcare} = useLivePreview({initialData: initialData.value, depth: 3})

if (!healthcare.value) {
  throw createError({status: 503, message: 'Could not load the page.'})
}

const pixelGridLg = [
  '  111111111111111111111111111111111111111111111   ',
  ' 1111111111111111     111111211111111111111111    ',
  '  112221111111122111111111221211111111111111111   ',
  '       211111111111111111111111111111    2111111  ',
  '       211111111111111111111111111111    2111111  ',
  '                                    1    2111111  ',
  '       2111111                     11    2111111  ',
  '1111                              111 221111111   ',
  '1111111111                            11 221111111',
  '1111111111                                        ',
  '                                    2111 221111111',
  '                                   22111 221111111',
  '                                  122111 221111111',
  '                                      11 221111111',
].join('\n')

useSchemaOrg([
  {
    '@type': 'FAQPage',
    mainEntity: computed(
      () =>
        resolveRelations(healthcare.value?.faq?.questions).map(item => ({
          '@type': 'Question',
          name: item.title,
          acceptedAnswer: {
            '@type': 'Answer',
            text: lexicalToPlaintext(item.body),
          },
        })) || []
    ),
  },
])

const scrollToElement = async (elementId: string) => {
  await nextTick()
  const element = document.querySelector(`#${elementId}`)?.scrollIntoView({behavior: 'smooth'})
}

const seo = healthcare.value?.meta

useSeoMeta({
  title: seo?.title,
  description: seo?.description,
  ogUrl: `${baseUrl}/custom-healthcare-software-development`,
  ogTitle: seo?.ogTitle || seo?.title,
  ogDescription: seo?.ogDescription || seo?.description,
  ogImage: resolveMediaUrl(seo?.image),
  ogImageAlt: resolveMediaAlt(seo?.image),
})

const gradientClass = 'text-transparent bg-clip-text leading-tight bg-gradient-to-l from-gray-950 to-gray-400'
const buttonBaseClass = 'flex w-full items-center justify-center py-2 text-sm font-normal md:w-28 lg:w-32 lg:text-md'
</script>

<template>
  <Container
    v-if="healthcare"
    class="screen-content relative flex flex-col items-center space-y-6 pb-16 pt-32 lg:pb-16 lg:pt-48"
  >
    <ReviewsSmall class="py-0 text-center lg:text-left" />

    <Heading :class="gradientClass" size="display" as="h1" class="w-full text-center xs:mb-8 lg:mb-4">
      <span :class="cx('pr-2')">{{ $t('healthcare.title') }}</span>
      <br />
      <span>{{ $t('healthcare.sub_title') }}</span>
    </Heading>

    <Paragraph
      v-if="healthcare.hero"
      class="mb-8 text-center text-sm text-gray-600 xs:px-12 xs:text-center lg:w-2/4 lg:px-0"
      size="medium"
    >
      {{ healthcare.hero.body }}
    </Paragraph>

    <div class="flex flex-col sm:justify-center lg:justify-start">
      <div class="flex flex-col space-x-0 xs:space-y-4 md:flex-row md:space-x-4 lg:space-y-0">
        <Action :class="buttonBaseClass" intent="main" as="link" to="/contact">{{
          $t('healthcare.cta', 'Book free consultation')
        }}</Action>

        <Action :class="[buttonBaseClass, 'py-3']" intent="normal" @click.prevent="scrollToElement('solutions')">{{
          $t('logistics.hero_button')
        }}</Action>
      </div>
    </div>

    <div v-if="healthcare.testimonials?.partners?.length" class="mt-14">
      <Partners :title="false" :partners="healthcare.testimonials.partners" class="mt-28 px-8 pb-4 lg:px-0" />
    </div>

    <Container
      v-if="healthcare.solutions"
      id="solutions"
      class="screen-content mx-auto flex flex-col items-center border-t border-gray-200"
    >
      <ShadowBorder class="pb-16" position="top" />
      <Solutions :data="healthcare.solutions" />
    </Container>

    <Container v-if="healthcare.solutionCards?.length" class="screen-content px-2 lg:py-8">
      <HealthCards :cards="healthcare.solutionCards" />
    </Container>

    <MoreSection class="border-b-0 py-14 lg:my-24" />

    <Container class="screen-content px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
      <section class="mb-16 px-20 lg:mb-24">
        <div
          class="flex flex-col gap-8 text-center lg:flex-row lg:items-center lg:justify-between lg:gap-12 lg:text-left"
        >
          <div class="lg:w-1/2">
            <Heading as="h2" size="caption">{{ healthcare.benefitsSection?.eyebrow }}</Heading>
            <Heading as="h3" size="large" class="text-2xl sm:text-3xl lg:text-4xl">{{
              healthcare.benefitsSection?.title
            }}</Heading>
          </div>

          <div class="lg:w-1/2">
            <Paragraph class="text-base text-gray-600 sm:text-lg">{{ $t('healthcare.benefits.body') }}</Paragraph>
          </div>
        </div>
      </section>

      <div class="grid grid-cols-1 justify-items-center gap-4 sm:grid-cols-2 sm:gap-16 lg:grid-cols-3 xl:grid-cols-4">
        <Benefit v-for="(card, index) in healthcare.benefitsSection?.benefits" :key="index" :data="card" />
      </div>
    </Container>

    <Comparison :comparison="healthcare.comparison" />

    <SingleTestimonial
      v-if="healthcare.singleTestimonial"
      :large="true"
      class="border-b-0 py-14 lg:py-24"
      :data="healthcare.singleTestimonial"
    />

    <Container v-if="healthcare.faq" class="screen-content flex flex-col items-center py-16 md:pb-16 lg:pb-10 lg:pt-16">
      <Heading size="caption" as="h2" class="text-center">{{ healthcare.faq.headline }}</Heading>
      <Heading as="h3" size="large" class="w-full text-center lg:w-1/2">{{ healthcare.faq.title }}</Heading>
      <Paragraph class="mt-2 w-full pb-6 text-left lg:w-1/2">{{ healthcare.faq.body }}</Paragraph>

      <Accordion
        v-if="healthcare.faq.questions"
        class="mt-8 pb-16 text-sm xs:px-12"
        :items="healthcare.faq.questions"
      />
    </Container>

    <BlogSection v-if="healthcare.blog" :data="healthcare.blog" />

    <LazyCta class="pt-24" :href="lp('/contact')" :label="$t('home.contact.ctaLabel')" />

    <div class="pointer-events-auto absolute inset-0 -z-10 h-full w-full px-4 md:px-8">
      <PixelGrid class="h-full" :pattern="pixelGridLg" />
    </div>
  </Container>
</template>
